// Extracted from original/src/App.tsx (ADR-08 §2.1):
//   card body        :14961-15034 (`case "wallets"` of `renderSettingsSection`; the frame/title come from SettingsCardFrame)
//   add dialog       :57068-57168 (`showAddWallet`)
//   rename dialog    :57169-57260 (`editingWallet`)
// Kept: every class string, icons and sizes, the backdrop / scale motion, the field order of both dialogs.
// Cut: `sortedWallets` (:446-462, vault first then digital; operator filter → the API's D-131 list), `handleAddWallet`
// (:8814-8907), `handleUpdateWallet` / `handleEditWallet` (:22515-22594), `handleDeleteWallet` (:22595-22684) →
// `onAdd` / `onRename` / `onArchive` (the container: `POST /boxes`, `PATCH /boxes`, `POST /boxes/archive`, own PIN,
// D04 §5); `parseNumeric(balance).toLocaleString()` → `balanceLabel`.
// Changed by decision (D04 §5, D-072, D-077, D-029): the hard-coded "محمي قطعيًا" protection by id 9 / name
// (:14988-15002, R-15) is not preserved; "delete" archives the box at a zero ledger balance (the trash icon stays, its
// title says archive); archived boxes are listed under the active ones with a restore button (new, D-072 "restorable");
// the type select of the add dialog offers only "رقمي" (manual boxes removed, D-077; vaults are created per branch by
// the chart of accounts, D-029). Added: data-testid attributes.
import { motion, AnimatePresence } from 'motion/react';
import { Edit2, Plus, RotateCcw, Trash2, Wallet as Wallet2, X } from 'lucide-react';
import { useT } from '../i18n-context.js';

export interface BoxRow {
  readonly id: string;
  readonly name: string;
  readonly kind: 'vault' | 'digital';
  readonly archived: boolean;
}

export interface BoxesCardProps {
  /** Active rows, vaults first (old `sortedWallets`). */
  readonly rows: readonly BoxRow[];
  readonly archivedRows: readonly BoxRow[];
  readonly onEdit: (id: string) => void;
  readonly onArchive: (id: string) => void;
  readonly onRestore: (id: string) => void;
  readonly onAdd: () => void;
}

export function BoxesCard(props: BoxesCardProps) {
  const t = useT();
  const typeLabel = (k: BoxRow['kind']) => (k === 'vault' ? t('boxes.type_vault') : t('boxes.type_digital'));
  const row = (w: BoxRow) => (
    <div key={w.id} data-testid={`box-row-${w.id}`} className="flex items-center justify-between p-3 bg-gray-50 dark:bg-[#05070a] rounded-xl border border-gray-100 dark:border-[#30363d] dark:digital-border">
      <div className="flex flex-col">
        <span className="font-black text-sm dark:text-[#f0f6fc] uppercase tracking-tight dark:digital-glow">{w.name}</span>
        <span className="text-[10px] text-gray-400 dark:text-gray-500 uppercase tracking-widest">{w.archived ? t('boxes.archived') : typeLabel(w.kind)}</span>
      </div>
      <div className="flex items-center gap-2">
        {w.archived ? (
          <button
            type="button"
            data-testid={`box-restore-${w.id}`}
            onClick={() => props.onRestore(w.id)}
            className="p-2 bg-white dark:bg-[#161b22] text-gray-400 hover:text-emerald-600 dark:hover:text-emerald-400 rounded-lg border border-gray-100 dark:border-[#30363d] transition-all"
            title={t('boxes.restore')}
          >
            <RotateCcw size={14} />
          </button>
        ) : (
          <>
            <button
              type="button"
              data-testid={`box-edit-${w.id}`}
              onClick={() => props.onEdit(w.id)}
              className="p-2 bg-white dark:bg-[#161b22] text-gray-400 hover:text-orange-600 dark:hover:text-orange-400 rounded-lg border border-gray-100 dark:border-[#30363d] transition-all"
              title={t('boxes.edit_name')}
            >
              <Edit2 size={14} />
            </button>
            {w.kind !== 'vault' && (
              <button
                type="button"
                data-testid={`box-archive-${w.id}`}
                onClick={() => props.onArchive(w.id)}
                className="p-2 bg-white dark:bg-[#161b22] text-gray-400 hover:text-red-600 dark:hover:text-red-400 rounded-lg border border-gray-100 dark:border-[#30363d] transition-all"
                title={t('boxes.archive')}
              >
                <Trash2 size={14} />
              </button>
            )}
          </>
        )}
      </div>
    </div>
  );
  return (
    <div className="space-y-4" data-testid="boxes-card">
      <div className="space-y-2">{props.rows.map(row)}</div>
      {props.archivedRows.length > 0 && <div className="space-y-2 opacity-70">{props.archivedRows.map(row)}</div>}
      <button
        type="button"
        data-testid="box-add"
        onClick={props.onAdd}
        className="w-full py-3 border-2 border-dashed border-gray-200 dark:border-[#30363d] text-gray-500 dark:text-[#8b949e] rounded-xl font-black uppercase tracking-widest hover:bg-gray-50 dark:hover:bg-[#161b22] transition-all flex items-center justify-center gap-2"
      >
        <Plus size={18} />
        {t('boxes.add')}
      </button>
    </div>
  );
}

export interface AddBoxDialogProps {
  readonly open: boolean;
  readonly name: string;
  readonly openingBalance: string;
  readonly onChange: (patch: { name?: string; openingBalance?: string }) => void;
  readonly onSubmit: () => void;
  readonly onClose: () => void;
  readonly busy?: boolean;
}

export function AddBoxDialog(props: AddBoxDialogProps) {
  const t = useT();
  return (
    <AnimatePresence>
      {props.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="box-add-dialog">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={props.onClose} className="absolute inset-0 bg-black/60 backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] w-full max-w-md rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border flex flex-col max-h-[90vh]"
          >
            <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-orange-50/50 dark:bg-orange-900/10">
              <h3 className="text-xl font-black flex items-center gap-2 dark:text-[#f0f6fc] uppercase tracking-tight dark:digital-glow">
                <Wallet2 size={24} className="text-orange-500" />
                {t('boxes.add')}
              </h3>
              <button type="button" onClick={props.onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-[#f0f6fc] transition-colors cursor-pointer">
                <X size={24} />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!props.busy) props.onSubmit();
              }}
              className="p-6 space-y-4 overflow-y-auto custom-scrollbar flex-1 text-right"
              dir="rtl"
            >
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest text-right">{t('boxes.name')}</label>
                <input
                  required
                  type="text"
                  data-testid="box-add-name"
                  value={props.name || ''}
                  onChange={(e) => props.onChange({ name: e.target.value })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500/20 dark:text-[#f0f6fc] font-black transition-all"
                  placeholder={t('boxes.name_placeholder')}
                />
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest text-right">{t('boxes.type')}</label>
                <select
                  value="digital"
                  onChange={() => undefined}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500/20 dark:text-[#f0f6fc] transition-all font-black text-right"
                >
                  <option value="digital">{t('boxes.type_digital_long')}</option>
                </select>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest text-right">{t('boxes.opening')}</label>
                <input
                  type="text"
                  inputMode="numeric"
                  data-testid="box-add-opening"
                  value={props.openingBalance || ''}
                  onChange={(e) => props.onChange({ openingBalance: e.target.value })}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500/20 dark:text-[#f0f6fc] font-black transition-all text-center font-mono text-lg"
                  placeholder="0"
                />
              </div>

              <button
                type="submit"
                data-testid="box-add-submit"
                disabled={props.busy}
                className="w-full py-4 bg-orange-600 text-white rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-orange-600/20 hover:bg-orange-700 transition-all cursor-pointer disabled:opacity-50"
              >
                {t('boxes.create')}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}

export interface EditBoxDialogProps {
  readonly open: boolean;
  readonly currentName: string;
  readonly newName: string;
  readonly balanceLabel: string;
  readonly kind: BoxRow['kind'];
  readonly onNewNameChange: (value: string) => void;
  readonly onSubmit: () => void;
  readonly onClose: () => void;
  readonly busy?: boolean;
}

export function EditBoxDialog(props: EditBoxDialogProps) {
  const t = useT();
  return (
    <AnimatePresence>
      {props.open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="box-edit-dialog">
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} onClick={props.onClose} className="absolute inset-0 bg-black/60 backdrop-blur-md" />
          <motion.div
            initial={{ scale: 0.9, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.9, opacity: 0 }}
            className="bg-white dark:bg-[#0d1117] w-full max-w-md rounded-3xl shadow-2xl relative z-10 overflow-hidden border border-gray-100 dark:border-[#30363d] dark:digital-border flex flex-col max-h-[90vh]"
          >
            <div className="p-6 border-b border-gray-100 dark:border-[#30363d] flex justify-between items-center bg-orange-50/50 dark:bg-orange-900/10">
              <h3 className="text-xl font-black flex items-center gap-2 dark:text-[#f0f6fc] uppercase tracking-tight dark:digital-glow">
                <Edit2 size={24} className="text-orange-500" />
                {t('boxes.edit_title')}
              </h3>
              <button type="button" onClick={props.onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-[#f0f6fc] transition-colors cursor-pointer">
                <X size={24} />
              </button>
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                if (!props.busy) props.onSubmit();
              }}
              className="p-6 space-y-4 overflow-y-auto custom-scrollbar flex-1 text-right"
              dir="rtl"
            >
              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest text-right">{t('boxes.current_name')}</label>
                <div className="w-full bg-gray-100 dark:bg-[#161b22] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 dark:text-gray-400 font-bold select-all text-right">{props.currentName}</div>
              </div>

              <div>
                <label className="block text-xs font-bold text-gray-400 dark:text-[#8b949e] mb-2 uppercase tracking-widest text-right">{t('boxes.new_name')}</label>
                <input
                  required
                  type="text"
                  data-testid="box-edit-name"
                  value={props.newName || ''}
                  onChange={(e) => props.onNewNameChange(e.target.value)}
                  className="w-full bg-gray-50 dark:bg-[#05070a] border border-gray-200 dark:border-[#30363d] rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-orange-500/20 dark:text-[#f0f6fc] font-black transition-all"
                  placeholder={t('boxes.new_name_placeholder')}
                />
              </div>

              <div className="grid grid-cols-2 gap-4 bg-gray-50 dark:bg-[#05070a] p-4 rounded-2xl border border-gray-100 dark:border-[#30363d]">
                <div>
                  <span className="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest text-right">{t('boxes.current_balance')}</span>
                  <span className="text-sm font-black dark:text-[#f0f6fc] font-mono block mt-1">{props.balanceLabel}</span>
                </div>
                <div>
                  <span className="block text-[10px] font-bold text-gray-400 dark:text-gray-500 uppercase tracking-widest text-right">{t('boxes.type_short')}</span>
                  <span className="text-sm font-black dark:text-[#f0f6fc] block mt-1">{props.kind === 'vault' ? t('boxes.type_vault') : t('boxes.type_digital')}</span>
                </div>
              </div>

              <button
                type="submit"
                data-testid="box-edit-submit"
                disabled={props.busy}
                className="w-full py-4 bg-orange-600 text-white rounded-2xl font-black uppercase tracking-widest shadow-lg shadow-orange-600/20 hover:bg-orange-700 transition-all cursor-pointer disabled:opacity-50"
              >
                {t('boxes.save_changes')}
              </button>
            </form>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
